import { Component, type ErrorInfo, type ReactNode } from "react";
import { useT } from "../i18n/use-t.js";

/**
 * 에러 바운더리 — 결정 #1(로컬 우선). 한 뷰에서 던져진 렌더 에러가 앱 전체를
 * 흰 화면으로 만들지 않도록 격리한다. 로컬 우선 앱에서 전체 크래시는 사용자가
 * 자기 데이터(대화·캐릭터)에도 접근 못 하게 만들기 때문에, 뷰 단위로 감싼다.
 *
 * 복구: "다시 시도"로 바운더리 내부를 리마운트한다. `resetKey`가 바뀌면(예: 활성
 * 뷰/채팅 전환) 자동으로 에러 상태를 초기화해 다른 화면으로 이동하면 회복된다.
 */
interface Props {
  children: ReactNode;
  /** 이 값이 바뀌면 에러 상태를 자동 리셋(뷰/채팅 전환 시). */
  resetKey?: unknown;
  /** 표시할 영역 이름(에러 메시지에 노출). */
  label?: string;
}

interface State {
  error: Error | null;
}

interface BoundaryMessages {
  fallbackLabel: string;
  problemSuffix: string;
  hint: string;
  retry: string;
}

export function ErrorBoundary(props: Props) {
  const { t } = useT();
  return (
    <ErrorBoundaryInner
      {...props}
      messages={{
        fallbackLabel: t("err.fallbackLabel"),
        problemSuffix: t("err.problemSuffix"),
        hint: t("err.hint"),
        retry: t("err.retry"),
      }}
    />
  );
}

class ErrorBoundaryInner extends Component<Props & { messages: BoundaryMessages }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 로컬 진단용 로그(외부 전송 없음, 결정 #1/#15).
    console.error("View render error", this.props.label, error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    // resetKey가 바뀌면 자동 회복(예: 다른 뷰로 이동).
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="text-sm font-semibold text-ink">
            {this.props.label ?? this.props.messages.fallbackLabel}
            {this.props.messages.problemSuffix}
          </div>
          <p className="max-w-md break-words text-xs text-ink-faint">
            {this.state.error.message}
          </p>
          <p className="max-w-md text-xs text-ink-faint">
            {this.props.messages.hint}
          </p>
          <button
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft"
            onClick={this.reset}
          >
            {this.props.messages.retry}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
